// Action/write cards: single, bulk (multi-action), tag pills and the
// write-proposal accordion group (WriteCard / MultiActionWriteCard /
// WriteProposalGroupCard)
// (WEB-8005). Composed by praxisStyles.ts at the section's original
// position — source order is part of the cascade.
export const praxisActionCards = `
/* Action card */
.pr-action-card {
  margin: 0;
  border: 1px solid var(--pr-hairline);
  border-radius: 3px;
  background: var(--pr-surface);
  overflow: hidden;
  transition: opacity 180ms ease;
}
.pr-action-card.resolved { opacity: 0.7; }
.pr-action-card-head {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 14px;
  background: var(--pr-surface-2);
  border-bottom: 1px solid var(--pr-hairline);
  font-size: 12px;
}
.pr-action-tag {
  font-family: var(--pr-font-mono);
  font-size: 10px; font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 2px;
  border: 1px solid currentColor;
  line-height: 1.4;
  flex-shrink: 0;
}
.pr-action-tag-create { color: oklch(0.42 0.11 155); }
.pr-action-tag-update { color: var(--pr-accent); }
.pr-action-tag-delete { color: oklch(0.48 0.16 25); }
.pr-action-card-entity {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--pr-ink-4);
  font-weight: 500;
}
.pr-action-card-status {
  margin-left: auto;
  font-size: 11px;
  color: var(--pr-success);
  font-weight: 500;
}
.pr-action-card-status.muted { color: var(--pr-ink-4); }
.pr-action-card-body { padding: 14px 16px 12px; }
.pr-action-card-title {
  font-family: var(--pr-font-serif);
  font-size: 16px; font-weight: 500;
  color: var(--pr-ink);
  letter-spacing: -0.005em;
}
.pr-action-card-quote {
  margin-top: 12px;
  padding: 10px 12px;
  background: var(--pr-surface-2);
  border-left: 2px solid var(--pr-ink-5);
  font-size: 13px; line-height: 1.55;
  white-space: pre-wrap;
  color: var(--pr-ink-2);
  font-family: var(--pr-font-serif);
  font-style: italic;
}
.pr-action-card-foot {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid var(--pr-hairline);
  background: var(--pr-surface);
}

/* Bulk card (multi-action) */
.pr-bulk-card {
  margin: 4px 0;
  border: 1px solid var(--pr-hairline);
  border-radius: 3px;
  background: var(--pr-surface);
  overflow: hidden;
  width: 100%;
}
.pr-bulk-card.resolved { opacity: 0.7; }
.pr-bulk-card-head {
  display: flex; flex-direction: column;
  padding: 12px 16px;
  background: var(--pr-surface);
}
.pr-bulk-card-head-row {
  display: flex; align-items: center; gap: 10px;
}
.pr-bulk-card-entity {
  font-size: 14px; color: var(--pr-ink); font-weight: 500;
}
.pr-bulk-card-count {
  margin-left: auto;
  font-size: 13px; color: var(--pr-ink-4);
  font-variant-numeric: tabular-nums;
}
.pr-bulk-card-summary {
  font-size: 13px; color: var(--pr-ink-2); line-height: 1.45;
  margin-top: 6px;
}
.pr-bulk-card-body {
  padding: 6px 16px 14px;
  display: flex; flex-direction: column; gap: 14px;
}
.pr-bulk-section-label {
  font-family: var(--pr-font-ui);
  font-size: 11.5px; font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--pr-ink-2);
}
.pr-bulk-section-head {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 10px;
}
/* The "what we're acting on" section (filter / selected ids) and the
 * "what we're setting" section (properties) read as the same thing at a
 * glance otherwise — same label style as the property row keys. Tint the
 * filter section background and use a slightly different label colour so
 * the two halves of a bulk action are visually distinct. */
.pr-bulk-section-filter {
  background: var(--pr-surface-2);
  border-radius: 4px;
  padding: 10px 12px;
  margin: 0 -2px;
}
.pr-bulk-section-filter .pr-bulk-section-label {
  color: var(--pr-info, #3c6db6);
}
.pr-bulk-section-properties .pr-bulk-section-label {
  color: var(--pr-ok, #2f7a4e);
}
.pr-bulk-add-inline {
  display: inline-flex; align-items: center;
  padding: 3px 10px;
  font-size: 12px;
  color: var(--pr-ink-4);
  background: transparent;
  border: 1px dashed var(--pr-hairline);
  border-radius: 999px;
  cursor: pointer;
  font-family: var(--pr-font-ui);
}
.pr-bulk-add-inline:hover { color: var(--pr-ink); border-color: var(--pr-ink-4); }
.pr-bulk-chips {
  display: flex; flex-wrap: wrap; gap: 6px; align-items: center;
}
.pr-bulk-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 4px 4px 10px;
  background: var(--pr-surface-2);
  border: 1px solid var(--pr-hairline);
  border-radius: 999px;
  font-size: 12.5px;
  color: var(--pr-ink);
  font-variant-numeric: tabular-nums;
}
.pr-bulk-chip-x {
  display: inline-grid; place-items: center;
  width: 18px; height: 18px;
  border: none; background: transparent;
  color: var(--pr-ink-4);
  cursor: pointer;
  border-radius: 50%;
  padding: 0; margin-left: 2px;
}
.pr-bulk-chip-x:hover { background: var(--pr-surface-3); color: var(--pr-ink); }
.pr-bulk-props {
  display: flex; flex-direction: column;
}
.pr-bulk-section--divided { border-top: 1px solid var(--pr-hairline); padding-top: 14px; }
.pr-bulk-prop-row {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 0;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  border-bottom: 1px solid var(--pr-hairline);
}
.pr-bulk-prop-row:last-child { border-bottom: none; }
.pr-bulk-prop-k {
  color: var(--pr-ink-4);
  font-size: 10.5px; font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  flex-shrink: 0;
}
.pr-bulk-prop-eq { display: none; }
.pr-bulk-prop-v {
  color: var(--pr-ink);
  min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.pr-bulk-prop-actions {
  display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0;
  margin-right: auto;
  visibility: hidden;
}
.pr-bulk-prop-row:hover .pr-bulk-prop-actions { visibility: visible; }
.pr-bulk-prop-v-btn {
  border: none; background: transparent; padding: 0;
  font: inherit; text-align: left; cursor: text;
  color: var(--pr-ink);
  border-radius: 2px;
}
.pr-bulk-prop-v-btn:hover { background: var(--pr-surface-2); box-shadow: 0 0 0 3px var(--pr-surface-2); }
.pr-bulk-prop-x {
  display: inline-grid; place-items: center;
  width: 22px; height: 22px;
  border: none; background: transparent;
  color: var(--pr-ink-4);
  cursor: pointer;
  border-radius: 4px;
}
.pr-bulk-prop-x:hover { background: var(--pr-surface-2); color: var(--pr-ink); }
.pr-bulk-add-btn {
  border: none; background: transparent;
  padding: 4px 6px;
  font-size: 12.5px;
  color: var(--pr-accent);
  cursor: pointer;
  font-family: var(--pr-font-ui);
  border-radius: 3px;
}
.pr-bulk-add-btn:hover { background: var(--pr-accent-soft); }
.pr-bulk-card-foot {
  display: flex; justify-content: flex-end; align-items: center;
  gap: 8px;
  padding: 10px 14px 12px;
  border-top: 1px solid var(--pr-hairline);
  background: var(--pr-surface);
}

/* Action-tag pills (bulk card) */
.pr-action-tag-pill {
  display: inline-flex; align-items: center;
  font-size: 11.5px; font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  letter-spacing: 0;
  text-transform: none;
  color: var(--pr-surface);
  background: var(--pr-ink);
}
.pr-action-tag-pill-create { background: oklch(0.42 0.12 155); }
.pr-action-tag-pill-update { background: var(--pr-ink); }
.pr-action-tag-pill-delete { background: oklch(0.50 0.17 25); }

/* Write proposal group (multiple proposals collapsed into one accordion) */
.pr-wgroup {
  margin-left: 44px;
  border: 1px solid var(--pr-hairline);
  border-radius: 3px;
  background: var(--pr-surface);
  overflow: hidden;
}
.pr-wgroup-head {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 14px;
  background: var(--pr-surface-2);
  border-bottom: 1px solid var(--pr-hairline);
  font-size: 12px;
}
.pr-wgroup-title {
  font-family: var(--pr-font-mono);
  font-size: 10.5px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--pr-ink-3);
  font-weight: 600;
}
.pr-wgroup-count {
  font-size: 11px;
  color: var(--pr-ink-4);
  font-variant-numeric: tabular-nums;
  padding: 1px 6px;
  border: 1px solid var(--pr-hairline);
  border-radius: 999px;
}
.pr-wgroup-summary {
  margin-left: auto;
  font-size: 11.5px;
  color: var(--pr-ink-4);
}

/* Chat-surface group wrapper: keeps the amber "Proposals to review" header
 * as an attention marker but drops the tinted background + border that made
 * the group read as a card-within-a-card. */
.pr-wgroup-shell {
  display: flex; flex-direction: column; gap: 10px;
  margin: 18px 0;
}
/* Keep the expanded card panel-white so the body doesn't visually invert
 * against the title head (which inherits the panel colour). */
.pr-wgroup-shell-list > .ws-prop-card--expanded {
  background: var(--ws-panel);
}
.pr-wgroup-shell-head {
  display: inline-flex; align-items: center; gap: 6px;
  color: var(--ws-warn, #cf8c34);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
}
.pr-wgroup-shell-head::before {
  content: "";
  display: inline-block;
  width: 8px; height: 8px;
  border-radius: 50%;
  background: var(--ws-warn, #cf8c34);
  box-shadow: 0 0 0 3px rgba(207, 140, 52, 0.18);
}
.pr-wgroup-shell-head .pr-wgroup-summary { color: var(--pr-ink-4); }
.pr-wgroup-shell-list {
  display: flex; flex-direction: column; gap: 10px;
}
.pr-wgroup-shell-list > .ws-prop-card,
.pr-wgroup-shell-list .ws-prop-card:last-child {
  border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-md);
  overflow: hidden;
}
`;
